import React from 'react';
import { DailyNotes, DailyRecord } from '../types';
import { FileEdit, AlertCircle, Smile, Meh, Frown, Sparkles, Battery, Eye } from 'lucide-react';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { toPersianDigits } from '../lib/storage';

interface NotesProps {
  record: DailyRecord;
  onUpdateRecord: (updated: DailyRecord) => void;
}

const MOOD_PRESETS = [
  { label: 'Calm', key: 'notesMood_Calm', display: '😌 آرامش', bg: 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300' },
  { label: 'Energetic', key: 'notesMood_Energetic', display: '⚡ پرانرژی', bg: 'bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300' },
  { label: 'Sore/Tense', key: 'notesMood_SoreTense', display: '😣 کوفته/گرفتگی', bg: 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300' },
  { label: 'Tired', key: 'notesMood_Tired', display: '🥱 خسته', bg: 'bg-indigo-50 dark:bg-indigo-950/20 text-indigo-700 dark:text-indigo-300' },
  { label: 'Anxious', key: 'notesMood_Anxious', display: '😰 مضطرب', bg: 'bg-pink-50 dark:bg-pink-950/20 text-pink-700 dark:text-pink-300' }
];

export default function NotesModule({ record, onUpdateRecord }: NotesProps) {
  const { lang, t } = useLanguage();
  
  const handleUpdateNotes = (field: keyof DailyNotes, value: any) => {
    onUpdateRecord({
      ...record,
      notes: {
        ...record.notes,
        [field]: value
      }
    });
  };

  const getPainLevelDescription = (lvl: number) => {
    if (lvl === 0) return t('notesPainDesc0', 'راحتی مطلق — بدون هرگونه درد و گرفتگی کمر');
    if (lvl <= 2) return t('notesPainDesc2', 'بسیار خفیف — درد جزئی اما کاملاً قابل نادیده گرفتن و کنترل');
    if (lvl <= 4) return t('notesPainDesc4', 'درد خفیف — احساس کوفتگی و گرفتگی دوره‌ای در مهره‌های پایین کمر');
    if (lvl <= 6) return t('notesPainDesc6', 'درد متوسط — تا حدی مانع انجام حرکات و چرخش‌های ناگهانی بدن می‌شود');
    if (lvl <= 8) return t('notesPainDesc8', 'درد شدید — پیاده‌روی یا نشستن طولانی دشوار است؛ حتماً از حامی کمر استفاده کنید');
    return t('notesPainDesc10', 'درد بسیار شدید — فاقد کارایی فیزیکی؛ سریعاً استراحت مطلق کنید');
  };

  return (
    <div id="notes-module-view" className="space-y-6 animate-fadeIn">
      
      {/* Header */}
      <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between border-b pb-4 border-gray-100 dark:border-gray-800">
        <div>
          <h2 className="text-2xl font-semibold font-display text-gray-900 dark:text-white flex items-center gap-2">
            <FileEdit className="w-6 h-6 text-indigo-500" />
            {t('notesHeaderTitle', 'دفترچه یادداشت‌ها و ارزیابی حالات روزانه')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('notesHeaderDesc', 'میزان درد، علائم فیزیکی، خلق‌وخو، انگیزه‌های زبانی، سطح انرژی و یادآوری‌های روزانه خود را مستند کنید.')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        
        {/* Left Form (8 cols) - Slider indicators and Mood blocks */}
        <div className="md:col-span-8 space-y-6">
          
          {/* Pain Slider 0-10 */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                {t('notesPainLevelTitle', 'سطح درد مهره‌های پایین کمر (بین ۰ تا ۱۰)')}
              </h3>
              <span className={`px-3 py-1 rounded-full text-xs font-bold font-mono ${
                record.notes.painLevel === 0 
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' 
                  : record.notes.painLevel <= 4 
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300' 
                    : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
              }`}>
                {t('notesPainScoreLabel', 'نمره نهایی درد: {{score}} از ۱۰', { score: formatNumberByLang(record.notes.painLevel, lang) })}
              </span>
            </div>

            <div className="space-y-2">
              <input 
                type="range" 
                min="0" 
                max="10" 
                value={record.notes.painLevel}
                onChange={(e) => handleUpdateNotes('painLevel', Number(e.target.value))}
                className="w-full accent-emerald-500 bg-gray-200 dark:bg-gray-700 h-2 rounded-lg cursor-pointer"
              />
              <div className="flex justify-between text-[10px] text-gray-400 font-bold uppercase font-mono">
                <span>{t('notesPainScale0', '۰ - بدون درد')}</span>
                <span>{t('notesPainScale5', '۵ - متوسط')}</span>
                <span>{t('notesPainScale10', '۱۰ - درد غیرقابل تحمل')}</span>
              </div>
            </div>

            <div className="p-3 bg-gray-50 dark:bg-gray-950 rounded-xl flex items-center gap-2 border border-gray-100 dark:border-gray-900">
              <Eye className="w-4 h-4 text-indigo-400 shrink-0" />
              <p className="text-xs text-gray-600 dark:text-gray-400 font-semibold font-sans">
                {t('notesPhysicalStatusPrefix', 'وضعیت فیزیکی:')} {getPainLevelDescription(record.notes.painLevel)}
              </p>
            </div>
          </div>

          {/* Mood Selector presets */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              {t('notesMoodTitle', 'خلق‌وخوی عمومی امروز شما')}
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {MOOD_PRESETS.map((p) => {
                const isSelected = record.notes.mood === p.label;
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => handleUpdateNotes('mood', p.label)}
                    className={`p-3.5 rounded-2xl flex flex-col items-center justify-center gap-1 border transition-all text-sm font-medium ${
                      isSelected 
                        ? `${p.bg} border-current shadow-sm scale-[1.03]` 
                        : 'border-gray-100 hover:border-gray-300 dark:border-gray-800 dark:hover:border-gray-700 bg-gray-50/20 dark:bg-gray-900/10 text-gray-700 dark:text-gray-400'
                    }`}
                  >
                    <span>{t(p.key, p.display)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Physical Energy Level meter (1-5) */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                <Battery className="w-4 h-4 text-amber-500" />
                {t('notesEnergyLevelTitle', 'میزان انرژی فیزیکی بدنی (بین ۱ تا ۵)')}
              </h3>
              <span className="text-xs font-bold font-mono text-gray-700 dark:text-gray-300">
                {t('notesEnergyScoreLabel', 'درجه نهایی: {{score}} از ۵', { score: formatNumberByLang(record.notes.energyLevel, lang) })}
              </span>
            </div>

            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((lvl) => {
                const isSelected = record.notes.energyLevel >= lvl;
                return (
                  <button
                    key={lvl}
                    type="button"
                    onClick={() => handleUpdateNotes('energyLevel', lvl)}
                    className={`flex-1 h-8 rounded-xl border transition-all ${
                      isSelected 
                        ? lvl <= 2 
                          ? 'bg-amber-400 border-amber-400 dark:bg-amber-500' 
                          : lvl <= 4 
                            ? 'bg-emerald-400 border-emerald-400 dark:bg-emerald-500' 
                            : 'bg-indigo-500 border-indigo-500' 
                        : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/20'
                    }`}
                    title={t('notesEnergyTooltip', 'درجه انرژی: {{level}}', { level: formatNumberByLang(lvl, lang) })}
                  />
                );
              })}
            </div>
          </div>

        </div>

        {/* Right Form (4 cols) - Free writing text comments */}
        <div className="md:col-span-4 space-y-4">
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider block">
              {t('notesNotebooksTitle', 'دفترچه‌های یادداشت ریکاوری و تحصیلی')}
            </h3>

            {/* Symptoms textbox */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase block">{t('notesSymptomsLabel', 'علائم فیزیکی فعال امروز')}</label>
              <textarea
                value={record.notes.symptoms}
                onChange={(e) => handleUpdateNotes('symptoms', e.target.value)}
                placeholder={t('notesSymptomsPlaceholder', 'مثال: گرفتگی عضلات صبحگاهی کمر، احساس گزگز خفیف در پای چپ هنگام ایستادن...')}
                rows={3}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white"
              />
            </div>

            {/* Observations textbox */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase block">{t('notesObservationsLabel', 'مشاهدات، درس‌ها و ایده‌ها')}</label>
              <textarea
                value={record.notes.observations}
                onChange={(e) => handleUpdateNotes('observations', e.target.value)}
                placeholder={t('notesObservationsPlaceholder', 'مثال: حرکات چرخش لگن قبل از خواب تسکین فوری بخشید. یادگیری کلمات سوئدی امروز ساده‌تر بود...')}
                rows={3}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white"
              />
            </div>

            {/* Reminders textbox */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase block">{t('notesRemindersLabel', 'یادآوری‌های شخصی')}</label>
              <textarea
                value={record.notes.reminders}
                onChange={(e) => handleUpdateNotes('reminders', e.target.value)}
                placeholder={t('notesRemindersPlaceholder', 'مثال: حتماً قبل از خروج به محل کار در ساعت ۰۷:۴۵ نیم لیتر آب بنوشم...')}
                rows={2}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white"
              />
            </div>

          </div>
        </div>

      </div>

    </div>
  );
}
